import { type ChangeEvent, useCallback } from 'react'

import { CloudUpload, Download, X } from 'lucide-react'

import { Button } from '@/components/ui/button'

export interface FileData {
	key: string
	name: string
}

const FileUploader = ({
	file,
	setFile,
	existingFile,
	setIsFileHidden,
	onDownload,
}: {
	file?: File | null
	setFile?: (file: File | null) => void
	existingFile?: FileData
	setIsFileHidden?: (value: boolean) => void
	onDownload?: () => void
}): JSX.Element => {
	const handleFileChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const selectedFile = event.target.files?.[0]
			if (selectedFile) {
				setFile?.(selectedFile)
				setIsFileHidden?.(false)
			}
		},
		[setFile, setIsFileHidden]
	)

	const handleRemove = useCallback(() => {
		if (file) {
			setFile?.(null)
		} else if (existingFile) {
			setIsFileHidden?.(true)
		}
	}, [file, setFile, existingFile, setIsFileHidden])

	const hasFile = !!file || !!existingFile

	return (
		<div className="relative mt-3 rounded-lg border-2 border-dashed border-border p-8 text-center">
			{hasFile && (
				<div className="absolute top-2 right-2 flex gap-1">
					{existingFile && !file && onDownload && (
						<Button
							variant="ghost"
							size="icon"
							onClick={onDownload}
							aria-label="Download existing file"
						>
							<Download className="h-4 w-4" />
						</Button>
					)}
					<Button
						variant="ghost"
						size="icon"
						onClick={handleRemove}
						aria-label="Remove file"
					>
						<X className="h-4 w-4" />
					</Button>
				</div>
			)}

			<label
				htmlFor="file-upload"
				className="mb-4 inline-flex cursor-pointer items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
			>
				<CloudUpload className="mr-2 h-4 w-4" />
				{hasFile ? 'Replace File' : 'Select File'}
			</label>
			<input
				id="file-upload"
				type="file"
				className="sr-only"
				onChange={handleFileChange}
			/>

			<p className="mt-2 text-sm text-muted-foreground">
				{file
					? file.name
					: existingFile
						? existingFile.name
						: 'No file selected'}
			</p>
		</div>
	)
}

export default FileUploader
